import React, { useCallback, useMemo } from 'react'
import { useWatch, useZionFormContext } from '@fs/zion-form'
import { Alert, FlowGrid, H6, InlineButton, Paragraph, Radio, RadioGroup, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getNestedOrFloatingElementById, useSlsStateContext } from '@fs/zion-sls'
import { MAKE_NEW_RECORD } from '../../familyMembersCard/utils/saveRelationships'
import PrimaryPersonInfoOverlay from './PrimaryPersonInfoOverlay'

const PersonalDetailsForm = () => {
  const [t] = useTranslation()
  const {
    formState: { errors, isSubmitted },
    setValue,
  } = useZionFormContext()
  const { images } = useSlsStateContext()
  const notSureOverlay = useOverlay()
  const [primaryPersonValue, recordId] = useWatch({ name: ['primaryPerson', 'recordId'] })

  const handlePrimaryPersonChange = useCallback(
    ({ target: { value } }) => {
      setValue('primaryPerson', value === 'true', { shouldDirty: true, shouldValidate: true })
    },
    [setValue]
  )

  // Check if the household has any other primary person
  const currentPersonId = useWatch({ name: 'id' })[0]

  const householdHasNoPrimary = useMemo(() => {
    if (primaryPersonValue !== false) return false
    if (recordId === MAKE_NEW_RECORD) return true
    if (!recordId) return false

    const record = getNestedOrFloatingElementById(images, recordId)
    if (!record) return false
    const hasOtherPrimary = record?.subElements?.some(
      (el) => el.elementType === 'PERSON' && el.primary === true && el.id !== currentPersonId
    )

    return !hasOtherPrimary
  }, [primaryPersonValue, recordId, images, currentPersonId])

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="nano">
      <H6>{t('essentialInformationForm.primaryPerson.title', 'Primary Person')}</H6>
      <>
        <Paragraph size="sm">
          {t('essentialInformationForm.primaryPerson.label', 'Is the document primarily about this person?')}{' '}
          <InlineButton
            onClick={notSureOverlay.open}
            linkName="primary-person-not-sure"
            data-testid="primary-person-not-sure-button"
          >
            <em>{t('essentialInformationForm.primaryPerson.notSure', 'Not sure?')}</em>
          </InlineButton>
        </Paragraph>
        <RadioGroup
          data-testid="primary-person-radio-group"
          orientation="horizontal"
          errorMessage={isSubmitted && errors?.primaryPerson?.message}
          dense
        >
          <Radio
            name="primaryPerson"
            value="true"
            checked={primaryPersonValue === true}
            label={t('essentialInformationForm.primaryPerson.yes', 'Yes')}
            onChange={handlePrimaryPersonChange}
          />
          <Radio
            name="primaryPerson"
            value="false"
            checked={primaryPersonValue === false}
            label={t('essentialInformationForm.primaryPerson.no', 'No')}
            onChange={handlePrimaryPersonChange}
          />
        </RadioGroup>

        {householdHasNoPrimary && (
          <Alert type="warning" data-testid="no-primary-person-warning">
            {t(
              'essentialInformationForm.primaryPerson.noPrimaryWarning',
              'This household will have no primary person.'
            )}
          </Alert>
        )}

        <PrimaryPersonInfoOverlay overlay={notSureOverlay} />
      </>
    </FlowGrid>
  )
}

export default PersonalDetailsForm
